import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FormProvider } from 'react-hook-form';
import { Typography } from '@components/Typography';
import { channelsActions, alertActions } from '@actions';
import { channelsService } from '@services';
import { CHANNEL } from '@constants/channels';
import LiveChatCode from '@features/ChannelsForms/LiveChatHTMLCode';
import FacebookForm from '@features/ChannelsForms/FacebookForm';
import SmsForm from '@features/ChannelsForms/SmsForm';
import EmailForm from '@features/ChannelsForms/EmailForm';
import WhatsAppForm from '@features/ChannelsForms/WhatsAppForm';
import LiveChatForm from '@features/ChannelsForms/LiveChatForm';
import WhatsAppBusinessForm from '@features/ChannelsForms/WhatsAppBusinessForm';
import { convertWorkingHours } from '@helpers/dates';
import { handleApiError } from '@helpers/api';
import { preparePhoneNumberForSubmit, setInitialPhoneNumberToForm } from '@helpers/phone';
import { useTranslation, useEditChannelsForm, useLocations } from '@hooks';
import { Modal, TabsWrapper, StyledRadioButtons } from './styles';

const Tab = {
    Settings: 0,
    Code: 1,
};

const EditModal = ({ open, setOpen, selectedChannel, setSelectedChannel, onClickDelete, accountLocale }) => {
    const { t } = useTranslation();

    const [tab, setTab] = useState(Tab.Settings);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const dispatch = useDispatch();
    const { mapSelectedLocationsToFormValue } = useLocations();
    const { getValues, setValue, control, reset, watch, trigger, isValid, isDirty } = useEditChannelsForm(
        selectedChannel?.type,
    );

    useEffect(() => {
        if (selectedChannel?.type === CHANNEL.SMS) {
            setValue('name', selectedChannel?.name || '', { shouldValidate: true });
            setValue(
                'phoneNumber',
                setInitialPhoneNumberToForm(selectedChannel?.smsParameters?.phoneNumber, accountLocale),
                { shouldValidate: true },
            );
            setValue('senderId', selectedChannel?.smsParameters?.senderId || '', { shouldValidate: true });
            setValue('provider', selectedChannel?.smsParameters?.provider || 0, { shouldValidate: true });
            setValue('accountId', selectedChannel?.smsParameters?.accountId || '', {
                shouldValidate: true,
            });
            setValue('apiKey', selectedChannel?.smsParameters?.apiKey || '', { shouldValidate: true });
            setValue('token', selectedChannel?.smsParameters?.token || '', { shouldValidate: true });
            setValue('login', selectedChannel?.smsParameters?.login || '', { shouldValidate: true });
            setValue('password', selectedChannel?.smsParameters?.password || '', { shouldValidate: true });
            return;
        }
        if (selectedChannel?.type === CHANNEL.WHATSAPP) {
            setValue('name', selectedChannel?.name || '', { shouldValidate: true });
            setValue('phoneNumber', selectedChannel?.whatsAppParameters?.phoneNumber || '', { shouldValidate: true });
            return;
        }
        if (selectedChannel?.type === CHANNEL.WHATSAPP_BUSINESS) {
            setValue('name', selectedChannel?.name || '', { shouldValidate: true });
            setValue('phoneNumber', selectedChannel?.whatsAppParameters?.phoneNumber || '', { shouldValidate: true });
            setValue('phoneNumberId', selectedChannel?.whatsAppParameters?.phoneNumberId || '', {
                shouldValidate: true,
            });
            setValue('phoneNumberName', selectedChannel?.whatsAppParameters?.phoneNumberName || '', {
                shouldValidate: true,
            });
            setValue('whatsAppBusinessId', selectedChannel?.whatsAppParameters?.whatsAppBusinessId || '', {
                shouldValidate: true,
            });
            return;
        }
        if (selectedChannel?.type === CHANNEL.LIVE_CHAT) {
            setValue('name', selectedChannel?.name || '', { shouldValidate: true });
            setValue('smsChannelId', selectedChannel?.liveChatParameters?.smsChannelId || '');
            setValue('emailChannelId', selectedChannel?.liveChatParameters?.emailChannelId ?? 'PLUSPOINT_EMAIL');
            setValue('useDefaultEmailChannel', selectedChannel?.liveChatParameters?.useDefaultEmailChannel);
            setValue('labels', selectedChannel?.labels || []);
            setValue(
                'notifyAboutMissingConversations',
                selectedChannel?.liveChatParameters?.notifyAboutMissingConversations,
                { shouldValidate: true },
            );
            setValue('welcomeHeading', selectedChannel?.liveChatParameters?.welcomeHeading || '', {
                shouldValidate: true,
            });
            setValue('welcomeTagline', selectedChannel?.liveChatParameters?.welcomeTagline || '', {
                shouldValidate: true,
            });
            setValue('workingHoursEnabled', selectedChannel?.liveChatParameters?.workingHoursEnabled, {
                shouldValidate: true,
            });
            setValue('offlineMessage', selectedChannel?.liveChatParameters?.offlineMessage || '', {
                shouldValidate: true,
            });
            setValue(
                'workingHours',
                { days: convertWorkingHours(selectedChannel?.liveChatParameters?.workingHours?.days || []) },
                { shouldValidate: true },
            );
            setValue('contactEmail', selectedChannel?.liveChatParameters?.fields?.Email.enable);
            setValue('contactEmailRequired', selectedChannel?.liveChatParameters?.fields?.Email.required);
            setValue('contactPhone', selectedChannel?.liveChatParameters?.fields?.Phone.enable);
            setValue('contactPhoneRequired', selectedChannel?.liveChatParameters?.fields?.Phone.required);
            setValue('contactName', selectedChannel?.liveChatParameters?.fields?.Name.enable);
            setValue('contactNameRequired', selectedChannel?.liveChatParameters?.fields?.Name.required);
            setValue('locationsEnabled', selectedChannel?.liveChatParameters?.locations?.length > 0);
            setValue(
                'locations',
                mapSelectedLocationsToFormValue(selectedChannel?.liveChatParameters?.locations || []) || [],
                { shouldValidate: true },
            );
            return;
        }
        if (selectedChannel?.type === CHANNEL.FACEBOOK || selectedChannel?.type === CHANNEL.INSTAGRAM) {
            setValue('name', selectedChannel?.name || '', { shouldValidate: true });
            setValue('locationId', selectedChannel?.locationId || '', { shouldValidate: true });
            setValue('id', selectedChannel?.id || '', { shouldValidate: true });
            selectedChannel?.type === CHANNEL.INSTAGRAM &&
                setValue('saveInstagramReplies', selectedChannel?.facebookParameters?.saveInstagramReplies, {
                    shouldValidate: true,
                });
            return;
        }
        if (selectedChannel?.type === CHANNEL.EMAIL) {
            setValue('name', selectedChannel?.name || '', { shouldValidate: true });
            setValue('provider', selectedChannel?.emailParameters?.provider || 0, {
                shouldValidate: true,
            });
            setValue('smtpHost', selectedChannel?.emailParameters?.smtpHost || '', {
                shouldValidate: true,
            });
            setValue('smtpPort', selectedChannel?.emailParameters?.smtpPort || '', {
                shouldValidate: true,
            });
            setValue('username', selectedChannel?.emailParameters?.username || '', {
                shouldValidate: true,
            });
            setValue('isSSL', selectedChannel?.emailParameters?.isSSL, { shouldValidate: true });
            setValue('email', selectedChannel?.emailParameters?.email || '', { shouldValidate: true });
            setValue('password', selectedChannel?.emailParameters?.password || '', {
                shouldValidate: true,
            });
            return;
        }
    }, [selectedChannel, setValue, accountLocale]);

    const onClickSubmit = async () => {
        setIsSubmitting(true);
        const values = getValues();

        if (selectedChannel?.type === CHANNEL.LIVE_CHAT) {
            if (!values?.contactEmailRequired && !values?.contactPhoneRequired) {
                dispatch(alertActions.error(t('apiErrors.phoneOrEmailRequired')));
                setIsSubmitting(false);
                return;
            } else if (values?.notifyAboutMissingConversations && !values?.smsChannelId && !values?.emailChannelId) {
                dispatch(alertActions.error(t('apiErrors.notificationChannelRequired')));
                setIsSubmitting(false);
                return;
            }
        }
        try {
            if (selectedChannel?.type === CHANNEL.SMS) {
                await channelsService.createOrUpdateChannelSms({
                    id: selectedChannel?.id,
                    name: values.name,
                    locationId: values.locationId || null,
                    brandId: values.brandId || null,
                    smsParameters: {
                        senderId: values?.senderId,
                        provider: values?.provider,
                        accountId: values?.accountId,
                        apiKey: values?.apiKey,
                        token: values?.token,
                        phoneNumber: preparePhoneNumberForSubmit(values.phoneNumber),
                        login: values?.login,
                        password: values?.password,
                    },
                });
            } else if (selectedChannel?.type === CHANNEL.WHATSAPP) {
                await channelsService.createOrUpdateChannelWhatsApp({
                    id: selectedChannel?.id,
                    name: values.name,
                    newWhatsAppAccount: selectedChannel?.newWhatsAppAccount,
                    locationId: values.locationId || null,
                    brandId: values.brandId || null,
                });
            } else if (selectedChannel?.type === CHANNEL.WHATSAPP_BUSINESS) {
                await channelsService.createOrUpdateChannelWhatsAppBusiness({
                    id: selectedChannel?.id,
                    name: values.name,
                    locationId: values.locationId || null,
                    brandId: values.brandId || null,
                    phoneNumber: values.phoneNumber,
                    phoneNumberId: values.phoneNumberId,
                    phoneNumberName: values.phoneNumberName,
                    whatsAppBusinessId: values.whatsAppBusinessId,
                });
            } else if (selectedChannel?.type === CHANNEL.LIVE_CHAT) {
                await channelsService.createOrUpdateChannelLiveChat({
                    id: selectedChannel?.id,
                    name: values.name,
                    locationId: values.locationId || null,
                    brandId: values.brandId || null,
                    labels: values.labels,
                    liveChatParameters: {
                        locations: values.locationsEnabled ? values.locations.map(item => item.id) : [],
                        smsChannelId: values.smsChannelId,
                        emailChannelId: values.emailChannelId === 'PLUSPOINT_EMAIL' ? null : values.emailChannelId,
                        useDefaultEmailChannel: values.emailChannelId === 'PLUSPOINT_EMAIL',
                        notifyAboutMissingConversations: values.notifyAboutMissingConversations,
                        welcomeHeading: values.welcomeHeading,
                        welcomeTagline: values.welcomeTagline,
                        workingHoursEnabled: values.workingHoursEnabled,
                        workingHours: { days: convertWorkingHours(values.workingHours.days, 'toUTC') },
                        offlineMessage: values.offlineMessage,
                        fields: {
                            Email: { type: 0, enable: values.contactEmail, required: values.contactEmailRequired },
                            Phone: { type: 1, enable: values.contactPhone, required: values.contactPhoneRequired },
                            Name: { type: 2, enable: values.contactName, required: values.contactNameRequired },
                        },
                    },
                });
            } else if (selectedChannel?.type === CHANNEL.FACEBOOK) {
                await channelsService.createOrUpdateChannelFacebook({
                    id: selectedChannel?.id,
                    ...values,
                });
            } else if (selectedChannel?.type === CHANNEL.INSTAGRAM) {
                await channelsService.createOrUpdateChannelInstagram({
                    id: selectedChannel?.id,
                    ...values,
                });
            } else if (selectedChannel?.type === CHANNEL.EMAIL) {
                await channelsService.createOrUpdateChannelEmail({
                    id: selectedChannel?.id,
                    name: values.name,
                    locationId: values.locationId || null,
                    brandId: values.brandId || null,
                    emailParameters: {
                        provider: values.provider,
                        smtpHost: values.smtpHost,
                        smtpPort: values.smtpPort,
                        username: values.username,
                        isSSL: values.isSSL,
                        email: values.email,
                        password: values.password,
                    },
                });
            }
            await dispatch(channelsActions.get());
            dispatch(alertActions.success(t('alertMessages.updateSuccess')));
            onClose();
        } catch (error) {
            handleApiError(error, 'alertMessages.updateFail', { amount: error.count });
        } finally {
            setIsSubmitting(false);
        }
    };

    const onClose = () => {
        setOpen(false);
        setSelectedChannel({});
        reset();
    };

    const getTitleText = () => {
        switch (selectedChannel?.type) {
            case CHANNEL.SMS:
                return t('common.sms');
            case CHANNEL.WHATSAPP:
            case CHANNEL.WHATSAPP_BUSINESS:
                return t('common.whatsApp');
            case CHANNEL.LIVE_CHAT:
                return t('common.liveChatLowercase');
            case CHANNEL.FACEBOOK:
                return t('common.facebookMessenger');
            case CHANNEL.INSTAGRAM:
                return t('common.instagram');
            case CHANNEL.EMAIL:
                return t('common.email');
            default:
                return '';
        }
    };

    const onDelete = () => onClickDelete(selectedChannel);

    return (
        <Modal
            isOpen={open}
            handleClose={onClose}
            title={t('SettingsChannels.editChannelTitle', { channelType: getTitleText() })}
            subtitle={
                <Typography variant="caption" color="textSecondary">
                    {t('SettingsChannels.editChannelSubtitle', { channelType: getTitleText() })}
                </Typography>
            }
            onPrimaryAction={onClickSubmit}
            primaryActionText={t('buttons.saveChanges')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            onSecondaryAction={onClose}
            secondaryActionText={t('buttons.cancel')}
            onAdditionalAction={onDelete}
            additionalTextButton={t('buttons.delete')}
        >
            {selectedChannel?.type === CHANNEL.LIVE_CHAT ? (
                <TabsWrapper>
                    <StyledRadioButtons
                        value={tab}
                        onChange={setTab}
                        options={[
                            { value: Tab.Settings, label: t('common.settings') },
                            { value: Tab.Code, label: t('ShowcaseWidgets.widgetCode') },
                        ]}
                    />
                </TabsWrapper>
            ) : null}
            <FormProvider
                {...{
                    control,
                    setValue,
                    getValues,
                    watch,
                    trigger,
                    twillioUid: selectedChannel?.id,
                    selectedChannel,
                }}
            >
                {selectedChannel?.type === CHANNEL.SMS ? <SmsForm isEdit /> : null}
                {selectedChannel?.type === CHANNEL.WHATSAPP ? <WhatsAppForm isEdit /> : null}
                {selectedChannel?.type === CHANNEL.WHATSAPP_BUSINESS ? <WhatsAppBusinessForm isEdit /> : null}
                {selectedChannel?.type === CHANNEL.LIVE_CHAT && tab === Tab.Settings ? <LiveChatForm /> : null}
                {selectedChannel?.type === CHANNEL.LIVE_CHAT && tab === Tab.Code ? (
                    <LiveChatCode liveChatPublicId={selectedChannel?.publicId} />
                ) : null}
                {selectedChannel?.type === CHANNEL.FACEBOOK || selectedChannel?.type === CHANNEL.INSTAGRAM ? (
                    <FacebookForm type={selectedChannel?.type} />
                ) : null}
                {selectedChannel?.type === CHANNEL.EMAIL ? <EmailForm /> : null}
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
